import { useEffect, useState, useCallback } from "react";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import { appConfirm } from "../../../components/ConfirmDialog";
import { Loader2, Ruler, ClipboardCheck, HardHat, Sparkles, Save, CheckCircle2, History, Plus, Trash2 } from "lucide-react";

const gbp = (n) => "£" + Number(n || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 });
const dtGB = (d) => d ? new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

function Section({ title, Icon, hint, children }) {
  return (
    <div className="border border-[#E5E7EB] bg-white">
      <div className="px-4 pt-3.5 pb-2 border-b border-[#F0EAE0]">
        <div className="text-xs font-black uppercase tracking-widest text-[#12263A] flex items-center gap-1.5">
          <Icon size={13} className="text-[#C79A3B]" /> {title}
        </div>
        {hint && <p className="text-[11px] text-[#9CA3AF] mt-0.5">{hint}</p>}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function SegBtn({ active, tone, onClick, testId, children }) {
  const tones = {
    green: active ? "bg-[#1F6B3B] text-white border-[#1F6B3B]" : "text-[#1F6B3B] border-[#B7DEC6] hover:bg-[#E7F4EC]",
    amber: active ? "bg-[#B45309] text-white border-[#B45309]" : "text-[#B45309] border-[#EAC7B4] hover:bg-[#FBF3EC]",
    red: active ? "bg-[#C0392B] text-white border-[#C0392B]" : "text-[#C0392B] border-[#F1D4CE] hover:bg-[#FEF6F5]",
  };
  return (
    <button data-testid={testId} onClick={onClick}
      className={`px-2.5 py-1 text-[11px] font-bold border transition-colors ${tones[tone]}`}>
      {children}
    </button>
  );
}

const DIM_LABELS = { width: "Width", depth: "Depth", height: "Internal height" };

function Dimensions({ form, setForm, canReprice, isAdmin, reference, reload, refresh }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const dims = form.dimensions || {};
  const verified = (k) => dims[k]?.verified ?? "";
  const changed = ["width", "depth", "height"].some(k => {
    const d = dims[k] || {};
    return d.verified != null && d.verified !== "" && Number(d.verified) !== Number(d.configured);
  });
  const setDim = (k, v) => {
    setForm(f => ({ ...f, dimensions: { ...f.dimensions, [k]: { ...f.dimensions[k], verified: v === "" ? null : v } } }));
    setPreview(null);
  };
  const dimsPayload = () => Object.fromEntries(["width", "depth", "height"]
    .map(k => [k, Number(verified(k) !== "" && verified(k) != null ? verified(k) : dims[k]?.configured)]));

  const doPreview = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/booking/${reference}/survey/reprice`, { ...dimsPayload(), apply: false });
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not re-price"); }
    setBusy(false);
  };
  const doApply = async () => {
    if (!(await appConfirm({
      title: "Apply the new contract total?",
      description: `The contract total moves from ${gbp(preview.current_total)} to ${gbp(preview.new_total)} (${preview.delta >= 0 ? "+" : "−"}${gbp(Math.abs(preview.delta))}). The verified dimensions are saved to the job and the change is logged.`,
      confirmLabel: "Apply new price",
    }))) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/booking/${reference}/survey/reprice`, { ...dimsPayload(), apply: true });
      toast.success(`Re-priced — contract total now ${gbp(r.data.new_total)}`);
      setPreview(null);
      refresh(r.data.form);
      reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not apply the re-price"); }
    setBusy(false);
  };

  return (
    <Section title="Verify dimensions" Icon={Ruler} hint="Measure on site — correct any figure that differs from the configurator.">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {["width", "depth", "height"].map(k => (
          <div key={k} data-testid={`survey-dim-${k}`}>
            <div className="text-[11px] font-bold text-[#12263A]">{DIM_LABELS[k]}</div>
            <div className="text-[11px] text-[#9CA3AF] mb-1">Configured: {dims[k]?.configured}m</div>
            <div className="relative">
              <input data-testid={`survey-dim-${k}-input`} type="number" step="0.1" min="0" value={verified(k)}
                onChange={(e) => setDim(k, e.target.value)} placeholder={String(dims[k]?.configured ?? "")}
                className="w-full border border-[#E5E7EB] px-3 py-2 text-sm focus:border-[#C79A3B] outline-none" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#9CA3AF]">m</span>
            </div>
          </div>
        ))}
      </div>
      {changed && canReprice && (
        <div className="mt-3 border border-[#EAC7B4] bg-[#FBF3EC] p-3" data-testid="survey-reprice-panel">
          <div className="text-xs font-bold text-[#9A6B12] mb-2">Dimensions differ from the configurator — the job can be re-priced automatically.</div>
          {preview ? (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm" data-testid="survey-reprice-result">
                <span className="text-[#6B7280]">Contract total </span>
                <b className="tabular-nums">{gbp(preview.current_total)}</b>
                <span className="text-[#6B7280]"> → </span>
                <b className="tabular-nums text-[#12263A]">{gbp(preview.new_total)}</b>
                <span className={`ml-2 font-bold tabular-nums ${preview.delta >= 0 ? "text-[#C0392B]" : "text-[#1F6B3B]"}`}>
                  {preview.delta >= 0 ? "+" : "−"}{gbp(Math.abs(preview.delta))}
                </span>
              </div>
              {isAdmin ? (
                <button data-testid="survey-reprice-apply" onClick={doApply} disabled={busy}
                  className="px-3.5 py-2 text-xs font-bold bg-[#12263A] text-white hover:bg-[#0C1B2B] disabled:opacity-50">
                  {busy ? <Loader2 size={13} className="animate-spin inline mr-1" /> : null}Apply new price
                </button>
              ) : (
                <span className="text-[11px] font-bold text-[#9A6B12]" data-testid="survey-reprice-admin-only">
                  Ask an admin to apply the new total
                </span>
              )}
            </div>
          ) : (
            <button data-testid="survey-reprice-preview" onClick={doPreview} disabled={busy}
              className="px-3.5 py-2 text-xs font-bold border border-[#B45309] text-[#B45309] hover:bg-[#B45309] hover:text-white disabled:opacity-50">
              {busy ? <Loader2 size={13} className="animate-spin inline mr-1" /> : null}Preview new price
            </button>
          )}
        </div>
      )}
      {changed && !canReprice && (
        <p className="text-[11px] text-[#9A6B12] mt-2">No configurator data on this job — adjust the price via an invoice line item on the Money tab.</p>
      )}
    </Section>
  );
}

function Selections({ form, setForm }) {
  const upd = (key, patch) => setForm(f => ({
    ...f, selections: f.selections.map(s => s.key === key ? { ...s, ...patch } : s),
  }));
  return (
    <Section title="Confirm configurator selections" Icon={ClipboardCheck} hint="Walk each item through with the client — tick confirmed, or mark changed and note what to.">
      <div className="space-y-2">
        {(form.selections || []).map((s, i) => (
          <div key={s.key} data-testid={`survey-sel-${i}`} className="border border-[#F0EAE0] p-2.5">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="text-sm min-w-0">
                <span className="font-bold text-[#12263A]">{s.label}</span>
                <span className="text-[#6B7280]"> — {s.configured}</span>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <SegBtn tone="green" active={s.status === "confirmed"} testId={`survey-sel-${i}-confirmed`}
                  onClick={() => upd(s.key, { status: s.status === "confirmed" ? "pending" : "confirmed" })}>✓ Confirmed</SegBtn>
                <SegBtn tone="amber" active={s.status === "changed"} testId={`survey-sel-${i}-changed`}
                  onClick={() => upd(s.key, { status: s.status === "changed" ? "pending" : "changed" })}>Changed</SegBtn>
              </div>
            </div>
            {s.status === "changed" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                <input data-testid={`survey-sel-${i}-changedto`} value={s.changed_to}
                  onChange={(e) => upd(s.key, { changed_to: e.target.value })} placeholder="Changed to…"
                  className="border border-[#E5E7EB] px-3 py-2 text-sm focus:border-[#C79A3B] outline-none" />
                <input data-testid={`survey-sel-${i}-note`} value={s.note}
                  onChange={(e) => upd(s.key, { note: e.target.value })} placeholder="Note (optional)…"
                  className="border border-[#E5E7EB] px-3 py-2 text-sm focus:border-[#C79A3B] outline-none" />
              </div>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

function SiteChecks({ form, setForm }) {
  const upd = (key, patch) => setForm(f => ({
    ...f, site_checks: f.site_checks.map(c => c.key === key ? { ...c, ...patch } : c),
  }));
  return (
    <Section title="Site checks" Icon={HardHat} hint="Flag anything that affects the build — issues are logged on the job.">
      <div className="space-y-2">
        {(form.site_checks || []).map(c => (
          <div key={c.key} data-testid={`survey-check-${c.key}`} className="border border-[#F0EAE0] p-2.5">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-sm font-bold text-[#12263A]">{c.label}</div>
              <div className="flex gap-1.5 shrink-0">
                <SegBtn tone="green" active={c.status === "ok"} testId={`survey-check-${c.key}-ok`}
                  onClick={() => upd(c.key, { status: c.status === "ok" ? "pending" : "ok" })}>✓ OK</SegBtn>
                <SegBtn tone="red" active={c.status === "issue"} testId={`survey-check-${c.key}-issue`}
                  onClick={() => upd(c.key, { status: c.status === "issue" ? "pending" : "issue" })}>Issue</SegBtn>
              </div>
            </div>
            {c.status === "issue" && (
              <input data-testid={`survey-check-${c.key}-note`} value={c.note}
                onChange={(e) => upd(c.key, { note: e.target.value })} placeholder="Describe the issue…"
                className="w-full border border-[#E5E7EB] px-3 py-2 text-sm mt-2 focus:border-[#C79A3B] outline-none" />
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

function Extras({ form, setForm, reference, saveForm, reload, isAdmin }) {
  const [desc, setDesc] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [ai, setAi] = useState(null);
  const [staffPrice, setStaffPrice] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [applying, setApplying] = useState(null);
  const [showApplied, setShowApplied] = useState(false);
  const pending = (form.extras || []).filter(x => !x.added_to_invoice);
  const applied = (form.extras || []).filter(x => x.added_to_invoice);

  const pruneExtra = async (ex) => {
    if (!(await appConfirm({
      title: `Remove "${ex.description}" from the survey?`,
      description: "The invoice line is NOT affected — remove that separately on the Money tab if needed. The removal is logged.",
      confirmLabel: "Remove",
    }))) return;
    try {
      const r = await api.delete(`/admin/booking/${reference}/survey/extras/${ex.id}`);
      setForm(r.data.form);
      toast.success("Extra removed — logged");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };

  const priceIt = async () => {
    if (desc.trim().length < 5) { toast.error("Describe the extra work first"); return; }
    setAiBusy(true); setAi(null);
    try {
      const r = await api.post(`/admin/booking/${reference}/survey/price-extra`, { description: desc.trim() });
      setAi(r.data);
      setStaffPrice(String(r.data.client_price));
      setAgreed(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI pricing failed"); }
    setAiBusy(false);
  };

  const addExtra = async () => {
    const ex = {
      id: crypto.randomUUID(), description: desc.trim(),
      ai_price: ai.client_price, ai_labour: ai.labour_cost, ai_materials: ai.materials_cost,
      ai_reasoning: ai.reasoning, staff_price: Number(staffPrice) || ai.client_price,
      client_agreed: agreed, added_to_invoice: false,
    };
    const nf = { ...form, extras: [...(form.extras || []), ex] };
    setForm(nf);
    setDesc(""); setAi(null); setStaffPrice(""); setAgreed(false);
    await saveForm(nf);
  };

  const updExtra = (id, patch) => setForm(f => ({
    ...f, extras: f.extras.map(x => x.id === id ? { ...x, ...patch } : x),
  }));
  const removeExtra = (id) => setForm(f => ({ ...f, extras: f.extras.filter(x => x.id !== id) }));

  const applyExtra = async (ex) => {
    const price = Number(ex.staff_price) || Number(ex.ai_price) || 0;
    if (!ex.client_agreed) { toast.error("Tick 'Client agreed' first"); return; }
    if (!(await appConfirm({
      title: `Add "${ex.description}" to the invoice at ${gbp(price)}?`,
      description: `${Number(ex.staff_price) !== Number(ex.ai_price) ? `AI suggested ${gbp(ex.ai_price)}; the staff price of ${gbp(price)} will be used. ` : ""}The contract total updates instantly and the change is logged.`,
      confirmLabel: "Add to invoice",
    }))) return;
    setApplying(ex.id);
    try {
      await api.put(`/admin/booking/${reference}/survey`, { form });
      const r = await api.post(`/admin/booking/${reference}/survey/extras/apply`, { extra_ids: [ex.id] });
      setForm(r.data.form);
      toast.success(`Added — contract total now ${gbp(r.data.total_inc_vat)}`);
      reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add to the invoice"); }
    setApplying(null);
  };

  return (
    <Section title="Client-requested extras (AI-priced)" Icon={Sparkles}
      hint="Type what the client wants — the AI prices it at current UK rates (inc VAT). Adjust the price before it goes on the invoice.">
      <div className="flex gap-2 flex-wrap">
        <input data-testid="survey-extra-desc" value={desc} onChange={(e) => setDesc(e.target.value)}
          placeholder="e.g. Add two double sockets and an outside tap on the rear wall…"
          className="flex-1 min-w-[220px] border border-[#E5E7EB] px-3 py-2 text-sm focus:border-[#C79A3B] outline-none" />
        <button data-testid="survey-extra-ai-btn" onClick={priceIt} disabled={aiBusy}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold bg-[#C79A3B] text-white hover:bg-[#A87E28] disabled:opacity-50">
          {aiBusy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Get AI price
        </button>
      </div>

      {ai && (
        <div className="mt-3 border border-[#C79A3B] bg-[#FFFDF8] p-3" data-testid="survey-ai-result">
          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div>
              <div className="label-eyebrow">AI price (inc VAT)</div>
              <div className="text-2xl font-black text-[#12263A] tabular-nums" data-testid="survey-ai-price">{gbp(ai.client_price)}</div>
              <div className="text-[11px] text-[#6B7280]">Labour {gbp(ai.labour_cost)} · Materials {gbp(ai.materials_cost)}</div>
            </div>
            <div className="text-right">
              <div className="label-eyebrow">Staff price (editable)</div>
              <div className="relative inline-block">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-[#9CA3AF]">£</span>
                <input data-testid="survey-extra-staff-price" type="number" step="1" value={staffPrice}
                  onChange={(e) => setStaffPrice(e.target.value)}
                  className="w-32 border border-[#E5E7EB] pl-6 pr-2 py-2 text-sm text-right tabular-nums focus:border-[#C79A3B] outline-none" />
              </div>
            </div>
          </div>
          {ai.reasoning && <p className="text-[11px] text-[#6B7280] mt-2">{ai.reasoning}</p>}
          {(ai.assumptions || []).length > 0 && (
            <ul className="text-[11px] text-[#9CA3AF] mt-1 list-disc pl-4">
              {ai.assumptions.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          )}
          <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
            <label className="flex items-center gap-2 text-xs font-bold text-[#12263A]">
              <input data-testid="survey-extra-agreed" type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
              Client agreed to this price on site
            </label>
            <button data-testid="survey-extra-add" onClick={addExtra}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold bg-[#12263A] text-white hover:bg-[#0C1B2B]">
              <Plus size={13} /> Save extra to survey
            </button>
          </div>
        </div>
      )}

      {pending.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="survey-extras-list">
          {pending.map((ex, i) => (
            <div key={ex.id} data-testid={`survey-extra-${i}`} className="border border-[#F0EAE0] p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm font-bold text-[#12263A]">{ex.description}</div>
                <button data-testid={`survey-extra-${i}-remove`} onClick={() => removeExtra(ex.id)}
                  className="text-[#9CA3AF] hover:text-[#C0392B] p-1 shrink-0"><Trash2 size={14} /></button>
              </div>
              <div className="flex items-center gap-4 mt-1.5 flex-wrap text-xs">
                <span className="text-[#6B7280]">AI: <b className="tabular-nums" data-testid={`survey-extra-${i}-ai`}>{gbp(ex.ai_price)}</b></span>
                <span className="text-[#6B7280] flex items-center gap-1">Staff:
                  <span className="relative inline-block">
                    <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-[#9CA3AF]">£</span>
                    <input data-testid={`survey-extra-${i}-price`} type="number" value={ex.staff_price ?? ""}
                      onChange={(e) => updExtra(ex.id, { staff_price: e.target.value === "" ? null : Number(e.target.value) })}
                      className="w-24 border border-[#E5E7EB] pl-4 pr-1 py-1 text-xs text-right tabular-nums" />
                  </span>
                </span>
                <label className="flex items-center gap-1.5 font-bold text-[#12263A]">
                  <input data-testid={`survey-extra-${i}-agreed`} type="checkbox" checked={!!ex.client_agreed}
                    onChange={(e) => updExtra(ex.id, { client_agreed: e.target.checked })} /> Client agreed
                </label>
                <span className="ml-auto">
                  <button data-testid={`survey-extra-${i}-apply`} onClick={() => applyExtra(ex)} disabled={applying === ex.id}
                    className="px-3 py-1.5 text-[11px] font-bold bg-[#1F6B3B] text-white hover:bg-[#155029] disabled:opacity-50">
                    {applying === ex.id ? <Loader2 size={11} className="animate-spin inline mr-1" /> : null}Add to invoice
                  </button>
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
      {applied.length > 0 && (
        <div className="mt-3" data-testid="survey-extras-applied">
          <button data-testid="survey-extras-applied-toggle" onClick={() => setShowApplied(v => !v)}
            className="text-[11px] font-black uppercase tracking-widest text-[#1F6B3B] hover:underline">
            ✓ Applied to invoice ({applied.length}) {showApplied ? "▾" : "▸"}
          </button>
          {showApplied && (
            <div className="mt-2 space-y-1.5">
              {applied.map((ex, i) => (
                <div key={ex.id} data-testid={`survey-applied-${i}`}
                  className="flex items-center justify-between gap-2 border border-[#D8EED8] bg-[#F7FBF8] px-3 py-2 text-xs flex-wrap">
                  <span className="font-bold text-[#12263A]">{ex.description}</span>
                  <span className="flex items-center gap-3">
                    <span className="text-[#6B7280]">AI {gbp(ex.ai_price)} · Charged <b className="tabular-nums">{gbp(ex.staff_price || ex.ai_price)}</b></span>
                    {isAdmin && (
                      <button data-testid={`survey-applied-${i}-remove`} onClick={() => pruneExtra(ex)}
                        title="Remove from survey (invoice line untouched)"
                        className="text-[#9CA3AF] hover:text-[#C0392B] p-0.5"><Trash2 size={13} /></button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <p className="text-[11px] text-[#9CA3AF] mt-3">
        Client documents carry the note: “Any additional works priced during a site visit remain provisional until confirmed in writing by our office.”
      </p>
    </Section>
  );
}

export function SurveyForm({ reference, role, reload }) {
  const isAdmin = role === "admin" || role === "senior_admin";
  const [form, setForm] = useState(null);
  const [canReprice, setCanReprice] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showLog, setShowLog] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/booking/${reference}/survey`).then(r => {
      setForm(r.data.form);
      setCanReprice(r.data.can_reprice);
    }).catch(() => toast.error("Could not load the survey form"));
  }, [reference]);
  useEffect(() => { load(); }, [load]);

  const saveForm = async (f = form, complete = false) => {
    setSaving(true);
    try {
      const r = await api.put(`/admin/booking/${reference}/survey`, { form: f, complete });
      setForm(r.data.form);
      toast.success(complete ? "Survey completed — all changes logged" : "Survey saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the survey"); }
    setSaving(false);
  };

  const completeSurvey = async () => {
    if (!(await appConfirm({
      title: "Mark this survey form as completed?",
      description: "Your name and the time are recorded. You can still edit afterwards — further edits stay in the change log.",
      confirmLabel: "Complete survey",
    }))) return;
    await saveForm(form, true);
  };

  if (!form) return <div data-testid="jobfile-survey" className="py-10 text-center text-sm text-[#6B7280]">Loading survey…</div>;

  return (
    <div data-testid="jobfile-survey" className="space-y-4">
      {form.status === "completed" && (
        <div className="border border-[#B7DEC6] bg-[#E7F4EC] px-4 py-2.5 text-xs font-bold text-[#1F6B3B]" data-testid="survey-completed-banner">
          ✓ Survey completed by {form.completed_by} on {dtGB(form.completed_at)} — later edits keep being logged
        </div>
      )}

      <Dimensions form={form} setForm={setForm} canReprice={canReprice} isAdmin={isAdmin}
        reference={reference} reload={reload} refresh={(f) => setForm(f)} />
      <Selections form={form} setForm={setForm} />
      <SiteChecks form={form} setForm={setForm} />
      <Extras form={form} setForm={setForm} reference={reference} saveForm={(f) => saveForm(f)} reload={reload} isAdmin={isAdmin} />

      <Section title="General notes & completion" Icon={ClipboardCheck}>
        <textarea data-testid="survey-notes" value={form.general_notes || ""} rows={4}
          onChange={(e) => setForm(f => ({ ...f, general_notes: e.target.value }))}
          placeholder="Anything else worth recording from the visit…"
          className="w-full border border-[#E5E7EB] px-3 py-2 text-sm focus:border-[#C79A3B] outline-none" />
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <button data-testid="survey-save" onClick={() => saveForm()} disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold bg-[#12263A] text-white hover:bg-[#0C1B2B] disabled:opacity-50">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save survey
          </button>
          {form.status !== "completed" && (
            <button data-testid="survey-complete" onClick={completeSurvey} disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold bg-[#1F6B3B] text-white hover:bg-[#155029] disabled:opacity-50">
              <CheckCircle2 size={13} /> Complete survey
            </button>
          )}
        </div>
      </Section>

      <div className="border border-[#E5E7EB] bg-white p-4" data-testid="survey-change-log">
        <button onClick={() => setShowLog(v => !v)} data-testid="survey-change-log-toggle"
          className="label-eyebrow flex items-center gap-1.5 hover:text-[#C79A3B]">
          <History size={12} /> Change log ({(form.change_log || []).length}) {showLog ? "▾" : "▸"}
        </button>
        {showLog && (
          (form.change_log || []).length === 0 ? (
            <p className="text-xs text-[#9CA3AF] mt-2">No changes logged yet.</p>
          ) : (
            <ul className="mt-2 space-y-1.5">
              {[...form.change_log].reverse().map((e, i) => (
                <li key={`${e.at}-${i}`} className="text-xs border-b border-[#F0EAE0] pb-1.5 last:border-0">
                  <span className="text-[#374151]">{e.summary}</span>
                  <span className="text-[#9CA3AF]"> · {e.by} · {dtGB(e.at)}</span>
                </li>
              ))}
            </ul>
          )
        )}
      </div>
    </div>
  );
}
